import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { TokenUserInfo } from '../common-structures/token-user-info';

@Component({
    selector: 'app-update-token',
    templateUrl: './app-update-token.component.html',
    styleUrls: ['./app-update-token.component.css'],
    standalone: false
})
export class AppUpdateTokenComponent implements OnInit, OnDestroy {
	tokenSubscription: Subscription;
	refreshIntervalSubscription: Subscription;
	offeset: number = 60;
	private initialValidityChecked = false;

	constructor(private _matDialog: MatDialog, private router: Router, public cmnServ: CommonService) {}

	/**
	 * @description refresh the token based on its expiration time (5 minutes before it expires)
	 */
	ngOnInit() {
		// Set up token refresh interval
		this.tokenSubscription = this.cmnServ.getOauthToken().subscribe((_token) => {
			if (_token && _token.expires_in && _token.access_token) {
				// Validate only once on startup. Re-validating on every emission (which includes
				// each refresh) can log an idle user out before the real token expiry.
				if (!this.initialValidityChecked) {
					this.initialValidityChecked = true;
					this.checkTokenValidityOnInit();
				}

				// Set up refresh timer based on token expiration
				this.setupRefreshTimer(_token);
			} else {
				// Token cleared (e.g., logout) — reset so the next login re-runs the startup validity
				// check, and stop the refresh timer tied to the now-removed token.
				this.initialValidityChecked = false;
				if (this.refreshIntervalSubscription) {
					this.refreshIntervalSubscription.unsubscribe();
				}
			}
		});
	}

	/**
	 * @description setup refresh timer based on token expiration time
	 */
	private setupRefreshTimer(token: any) {
		// Clean up any existing interval first
		if (this.refreshIntervalSubscription) {
			this.refreshIntervalSubscription.unsubscribe();
		}

		const refreshBufferMs = 5 * 60 * 1000; // refresh 5 minutes before expiry
		const expiresInMs = Number(token?.expires_in) > 0 ? Number(token.expires_in) * 1000 : NaN;
		let timeToExpiryMs = NaN;

		// Prefer the token's actual exp claim; expires_in can disagree with the real JWT expiry.
		try {
			const decodedToken: TokenUserInfo = jwt_decode(token.access_token);
			const expSeconds = Number(decodedToken?.exp);
			if (Number.isFinite(expSeconds)) {
				timeToExpiryMs = expSeconds * 1000 - Date.now();
			}
		} catch (error) {
			// Fall back to expires_in below.
		}

		// Fall back to expires_in when exp is missing/invalid; bail out if neither is usable
		// so we never pass a non-finite delay to interval().
		if (!Number.isFinite(timeToExpiryMs)) {
			timeToExpiryMs = expiresInMs;
		}
		if (!Number.isFinite(timeToExpiryMs)) {
			return;
		}

		// Refresh 5 minutes before expiry; if the token is already within that buffer (or past),
		// refresh almost immediately rather than waiting a fixed minimum that could overshoot expiry.
		const refreshTimeMs = Math.max(timeToExpiryMs - refreshBufferMs, 1000);

		// Set up single refresh timer based on token expiration
		this.refreshIntervalSubscription = interval(refreshTimeMs)
			.subscribe(() => this.refreshToken());
	}

	private checkTokenValidityOnInit() {
		this.cmnServ
			.getOauthToken()
			.pipe(take(1))
			.subscribe((token: any) => {
				if (token && token.access_token) {
					try {
						const decodedToken: TokenUserInfo = jwt_decode(token.access_token);
						const currentTime = new Date().getTime();
						const tokenExpiryTime = new Date(decodedToken.exp * 1000).getTime();
						
						// Only logout if the token is actually expired on startup. The refresh
						// timer is responsible for renewing a token that is still valid.
						if (tokenExpiryTime <= currentTime) {
							this.onLogout();
						}
					} catch (error) {
						// Don't auto-logout on decode errors during initialization
					}
				}
			});
	}

	refreshToken() {
		this.cmnServ
			.getOauthToken()
			.pipe(take(1))
			.subscribe((originalToken: any) => {
				if (!originalToken || !originalToken.access_token) {
					this.onLogout();
					return;
				}

				try {
					const decodedToken: TokenUserInfo = jwt_decode(originalToken.access_token);
					const currentTime = new Date().getTime();
					const tokenExpiryTime = new Date(decodedToken.exp * 1000).getTime();
					
					// If token is still valid, try to refresh it
					if (tokenExpiryTime > currentTime) {
						if (originalToken.refresh_token) {
							this.cmnServ.getRefreshToken(originalToken.refresh_token).subscribe(
								(refresh_token) => {
									this.cmnServ.setOauthToken(refresh_token);
									
									// Restart timer with new token's expiration time
									this.setupRefreshTimer(refresh_token);
								},
								(error) => {
									this.onLogout();
								}
							);
						} else {
							console.log('No refresh token available, logging out');
							this.onLogout();
						}
					} else {
						console.log('Token expired during refresh attempt, logging out');
						this.onLogout();
					}
				} catch (error) {
					console.error('Error decoding token during refresh:', error);
					this.onLogout();
				}
			});
	}

	onLogout() {
		this._matDialog.closeAll();
		
		// Use CommonService to clear all authentication data
		this.cmnServ.clearAuthenticationData();
		
		this.router.navigateByUrl('/');
	}

	ngOnDestroy() {
		if (this.tokenSubscription) {
			this.tokenSubscription.unsubscribe();
		}
		if (this.refreshIntervalSubscription) {
			this.refreshIntervalSubscription.unsubscribe();
		}
	}
}
